<script setup>
import { defineProps, defineEmits, computed } from "vue";
import Checkbox from "@/components/Checkbox/Checkbox.vue";
import RadioButton from "@/components/Checkbox/RadioButton.vue";

const props = defineProps({
  item: {
    type: [String, Number, Object],
    required: true
  },
  useCheckbox: {
    type: Boolean,
    default: false
  },
  useRadio: {
    type: Boolean,
    default: false
  },
  /**
   * Array для работы с Checkbox
   * String для работы с RadioButton
  **/
  modelValue: {
    type: [Array, String],
    default: () => []
  },
  valueKey: {
    type: String,
    default: "id"
  },
  activeValueKey: {
    type: [String, null],
    default: null
  },
  returnObject: {
    type: Boolean,
    default: false
  },
  isHover: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:modelValue"]);

// Вычисляемая функция для работы с локальным modelValue
const localModelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  }
});

// Автоматическое определение типа modelValue
const isArrayType = computed(() => {
  return Array.isArray(props.modelValue);
});

// Для radioButton проверяем строковое значение
const isRadioActive = computed(() => {
  return props.modelValue === itemValue.value;
});

// Функция получения значения для добавления в список
const itemValue = computed(() => {
  const item = props.item;
  if (props.returnObject) {
    return item;
  }
  if (typeof item === "object" && item !== null && props.valueKey in item) {
    return item[props.valueKey];
  }
  return item;
});

// Флаг. Является ли выбранный элемент активным
const isActive = computed(() => {
  // Если modelValue - строка
  if (!isArrayType.value) {
    return props.modelValue === itemValue.value;
  }

  // Если modelValue - массив
  if (props.activeValueKey) {
    return localModelValue.value.some(val => val.id === itemValue.value.id);
  }
  if (props.returnObject) {
    return localModelValue.value.some(val => JSON.stringify(val) === JSON.stringify(itemValue.value));
  }
  return localModelValue.value.includes(itemValue.value);
});

// Функция. Добавление/Удаление элемента в список
const toggle = () => {
  if (props.disabled) return;

  // Если modelValue - строка (Для работы с RadioButton)
  if (!isArrayType.value) {
    // Если уже выбран - ничего не делаем (стандартное поведение radio)
    if (isRadioActive.value) {
      return;
    }

    localModelValue.value = itemValue.value;
    return;
  }

  // Если modelValue - массив (Для работы с Checkbox)
  const current = [...localModelValue.value];
  const index = props.returnObject
      ? current.findIndex(val => JSON.stringify(val) === JSON.stringify(itemValue.value))
      : current.indexOf(itemValue.value);

  if (index > -1) {
    current.splice(index, 1);
  } else {
    current.push(itemValue.value);
  }

  localModelValue.value = current;
};

</script>

<template>
  <template v-if="props.useCheckbox">
    <Checkbox
      class="list__item"
      :class="{ 'list__item--active': isActive,
                'list__item--hover': isHover,
                'list__item--disabled': disabled }"
      :disabled="props.disabled"
      :model-value="isActive"
      :highlight-active-label="true"
      :input-hover="false"
      @change="toggle"
    >
      <slot />
    </Checkbox>
  </template>

  <template v-else-if="useRadio">
    <RadioButton
      class="list__item"
      :class="{ 'list__item--active': isActive,
                'list__item--hover': isHover,
                'list__item--disabled': disabled }"
      :disabled="props.disabled"
      :model-value="isRadioActive"
      name="myGroup"
      @change="toggle"
    >
      <slot />
    </RadioButton>
  </template>

  <template v-else>
    <li
      class="list__item"
      :class="{ 'list__item--active': isActive,
                'list__item--hover': isHover,
                'list__item--disabled': disabled }"
      role="option"
      :aria-selected="isActive"
      @click="toggle"
    >
      <slot />
    </li>
  </template>
</template>

<style scoped lang="scss">
.list__item {
  display: flex;
  padding: 6px 14px;
  gap: 8px;
  border-radius: 8px;
  color: var(--text-text-main);
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  line-height: 17px;
  overflow-wrap: break-word;
  word-break: break-word;

  &--disabled {
    color: var(--text-04);

    :deep(.checkbox__input) {
      color: var(--text-04);
    }
  }

  &--active:not(&--disabled) {
    color: var(--text-text-on);

    :deep(.checkbox__label) {
      color: var(--text-text-on);
    }

    &:hover {
      background-color: var(--background-bg-active-hover);
    }
  }

  &--hover {
    background-color: var(--background-bg-active-hover);
  }

  &--hover:not(&--active) {
    background-color: var(--background-bg-hover);
  }

  &:hover:not(&--active) {
    background-color: var(--background-bg-hover);
  }
}
</style>